import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Download, TrendingUp } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { salaryMarketAPI, SalaryMarketBucket, SalaryMarketFilters } from '../services/api';

function formatK(v: number | null | undefined): string {
  if (v == null || v <= 0) return '—';
  if (v >= 1000) return (Math.round(v / 1000)).toLocaleString('ru-RU') + ' тыс';
  return new Intl.NumberFormat('ru-RU').format(Math.round(v));
}

const EXPERIENCE_OPTIONS = [
  { value: '', label: 'Любой опыт' },
  { value: 'Нет опыта', label: 'Нет опыта' },
  { value: 'До 1 года', label: 'До 1 года' },
  { value: '1–3 года', label: '1–3 года' },
  { value: '3–6 лет', label: '3–6 лет' },
  { value: '3-6 лет', label: '3-6 лет (alt)' },
  { value: 'Более 6 лет', label: 'Более 6 лет' },
];

const SOURCE_OPTIONS = [
  { value: '', label: 'Любой источник' },
  { value: 'onboarding', label: 'Онбординг' },
  { value: 'landing', label: 'Лендинг' },
  { value: 'referral', label: 'Реферал' },
  { value: 'category', label: 'Категория' },
  { value: 'telegram_bot', label: 'Telegram Bot' },
  { value: 'admin', label: 'Admin' },
  { value: 'test', label: 'Тест' },
];

const SalaryMarketPage: React.FC = () => {
  const [items, setItems] = useState<SalaryMarketBucket[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState<SalaryMarketFilters>({
    role: '',
    city: '',
    experience: '',
    source: '',
    dateFrom: '',
    dateTo: '',
    minSamples: 5,
  });

  const load = useCallback(async (f: SalaryMarketFilters) => {
    try {
      setLoading(true);
      const activeFilters: SalaryMarketFilters = {};
      if (f.role) activeFilters.role = f.role;
      if (f.city) activeFilters.city = f.city;
      if (f.experience) activeFilters.experience = f.experience;
      if (f.source) activeFilters.source = f.source;
      if (f.dateFrom) activeFilters.dateFrom = f.dateFrom;
      if (f.dateTo) activeFilters.dateTo = f.dateTo;
      if (f.minSamples !== undefined) activeFilters.minSamples = f.minSamples;
      const data = await salaryMarketAPI.list(activeFilters);
      if (data.success) {
        setItems(data.items || []);
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    load(filters);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const activeFilters: SalaryMarketFilters = {};
      if (filters.role) activeFilters.role = filters.role;
      if (filters.city) activeFilters.city = filters.city;
      if (filters.experience) activeFilters.experience = filters.experience;
      if (filters.source) activeFilters.source = filters.source;
      if (filters.dateFrom) activeFilters.dateFrom = filters.dateFrom;
      if (filters.dateTo) activeFilters.dateTo = filters.dateTo;
      if (filters.minSamples !== undefined) activeFilters.minSamples = filters.minSamples;
      await salaryMarketAPI.downloadCsv(activeFilters);
      toast.success('CSV экспортирован');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Ошибка экспорта');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <TrendingUp className="h-7 w-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Зарплатный рынок</h1>
            <p className="text-sm text-gray-600">
              Агрегированные percentile-данные по salary submissions (минимум {filters.minSamples ?? 5} в bucket)
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting || loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Экспорт CSV
        </button>
      </div>

      {/* Filter bar */}
      <form onSubmit={handleSearch} className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <input
            type="text"
            placeholder="Должность"
            value={filters.role ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder="Город"
            value={filters.city ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={filters.experience ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, experience: e.target.value }))}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            {EXPERIENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={filters.source ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            type="date"
            placeholder="С даты"
            value={filters.dateFrom ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="date"
            placeholder="По дату"
            value={filters.dateTo ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-3 mt-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <span>Мин. выборка:</span>
            <input
              type="number"
              min={1}
              max={50}
              value={filters.minSamples ?? 5}
              onChange={(e) => setFilters((f) => ({ ...f, minSamples: Number(e.target.value) || 5 }))}
              className="border rounded-lg px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>
          <button
            type="submit"
            className="px-4 py-1.5 text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100"
          >
            Применить
          </button>
          <button
            type="button"
            onClick={() => {
              const reset: SalaryMarketFilters = { role: '', city: '', experience: '', source: '', dateFrom: '', dateTo: '', minSamples: 5 };
              setFilters(reset);
              load(reset);
            }}
            className="px-4 py-1.5 text-sm font-medium text-gray-600 border rounded-lg hover:bg-gray-50"
          >
            Сбросить
          </button>
          <span className="text-xs text-gray-400 ml-auto">
            {loading ? 'Загрузка...' : `${items.length} bucket${items.length === 1 ? '' : 'ов'}`}
          </span>
        </div>
      </form>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Должность</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canonical</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Город</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Опыт</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">n</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P25</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P50 (медиана)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P75</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">P90</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Среднее</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {items.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-10 text-center text-sm text-gray-500">
                      Нет bucket'ов с указанными фильтрами (или все с n &lt; {filters.minSamples ?? 5})
                    </td>
                  </tr>
                )}
                {items.map((item, idx) => (
                  <tr key={idx} className="hover:bg-blue-50">
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-[200px] truncate" title={item.bucket.role}>
                      {item.bucket.role || '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-[160px] truncate font-mono" title={item.bucket.roleCanonical}>
                      {item.bucket.roleCanonical || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {item.bucket.city || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {item.bucket.experience || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                      {item.n}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 whitespace-nowrap">
                      {formatK(item.p25)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-blue-700 whitespace-nowrap">
                      {formatK(item.p50)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 whitespace-nowrap">
                      {formatK(item.p75)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 whitespace-nowrap">
                      {formatK(item.p90)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-500 whitespace-nowrap">
                      {formatK(item.avg)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {items.length > 0 && (
            <div className="px-4 py-3 bg-gray-50 border-t text-xs text-gray-500">
              Данные: {items.length} bucket{items.length === 1 ? '' : 'ов'} · Суммарно{' '}
              {items.reduce((s, i) => s + i.n, 0)} submissions · Sorted by n DESC · Privacy: bucket &lt; {filters.minSamples ?? 5} скрыт
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SalaryMarketPage;
